import { useMemo, useRef, useState } from "react";
import { Icon } from "../ui/Icon";
import { STATUS_META, type TaskStatus } from "./taskMeta";
import "./Gantt.css";

/**
 * Gantt — Faz 2.
 *
 * Yerleşim: tek kaydırma bağlamı; görev adı kolonu `position: sticky; left: 0`,
 * tarih başlığı `sticky; top: 0`. Çubuklar zaman ekseninde MUTLAK konumlanır,
 * bağımlılık okları aynı koordinat uzayını kaplayan tek bir SVG katmanında çizilir
 * (`pointer-events: none` — okların altındaki çubuklar tıklanabilir kalsın).
 *
 * Tarihler YEREL gün olarak ele alınır (`localDayIndex`); UTC'ye çevirmek
 * UTC+3'te çubukları bir gün kaydırırdı — modülün tarih sözleşmesi yerel.
 */

export interface GanttTask {
  id: string;
  code: string;
  title: string;
  wbs: string;
  depth: number;
  status: string;
  progressPct: number;
  plannedStart: string | Date | null;
  plannedEnd: string | Date | null;
  isMilestone: boolean;
}

export interface GanttDep {
  id: string;
  predecessorId: string;
  successorId: string;
  type: string;
  violated: boolean;
}

/** Sunucudan gelen CPM sonucu (project.schedule.get). */
export interface GanttSchedule {
  taskId: string;
  slackDays: number | null;
  isCritical: boolean;
  unscheduled: boolean;
  constraintIncomplete?: boolean;
  earlyStart: string | Date | null;
  earlyFinish: string | Date | null;
}

/** Temel plan karşılaştırması (project.baseline.compare) — gölge çubuk için. */
export interface GanttBaseline {
  taskId: string;
  baseStart: string | Date | null;
  baseEnd: string | Date | null;
  endDrift: number | null;
}

interface Props {
  tasks: GanttTask[];
  deps: GanttDep[];
  schedule?: GanttSchedule[];
  baseline?: GanttBaseline[];
  baselineName?: string | null;
  showCritical: boolean;
  onToggleCritical: (v: boolean) => void;
  onOpenTask: (id: string) => void;
}

const ROW_H = 30;
const NAME_W = 260;
const SCALES = { day: 30, week: 12 } as const;
type Scale = keyof typeof SCALES;

/**
 * Gün indeksi ↔ tarih dönüşümü — TAM TERS olmalı.
 *
 * Önceki hâl epoch bölmesi kullanıyordu: UTC+3'te yerel gece yarısı
 * `D*86400000 - 10800000` olduğundan `floor` bir gün AŞAĞI yuvarlıyor ve
 * `dayFromIndex` bir önceki günü döndürüyordu. Çubuklar `localDayIndex` ile,
 * başlık/hafta sonu/ay etiketi `dayFromIndex` ile üretildiği için iki koordinat
 * sistemi bir gün kayıktı (çubuk "3"ün altındayken başlık "2" yazıyordu).
 *
 * Sabit bir YEREL epoch üzerinden gün aritmetiği bunu kesin çözer: `new Date(y, m, d + i)`
 * yerel takvimde normalize eder, yaz saati geçişinde de doğru kalır.
 */
const EPOCH_Y = 2000, EPOCH_M = 0, EPOCH_D = 1;
function localDayIndex(d: Date): number {
  const local = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const epoch = new Date(EPOCH_Y, EPOCH_M, EPOCH_D);
  // round: DST günleri 23/25 saat sürer, floor kullanırsak o günlerde kayar.
  return Math.round((local.getTime() - epoch.getTime()) / 86_400_000);
}
function dayFromIndex(i: number): Date {
  return new Date(EPOCH_Y, EPOCH_M, EPOCH_D + i);
}
const MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

export function GanttChart({ tasks, deps, schedule, baseline, baselineName, showCritical, onToggleCritical, onOpenTask }: Props) {
  const [scale, setScale] = useState<Scale>("day");
  const dayW = SCALES[scale];
  const scrollRef = useRef<HTMLDivElement>(null);

  // Zaman aralığı: planlanan tarihlerin sınırları + kenar payı. Hiç tarih yoksa
  // içinde bulunulan aya düşülür ki eksen boş kalmasın.
  const range = useMemo(() => {
    const idx: number[] = [];
    for (const t of tasks) {
      if (t.plannedStart) idx.push(localDayIndex(new Date(t.plannedStart)));
      if (t.plannedEnd) idx.push(localDayIndex(new Date(t.plannedEnd)));
    }
    // Temel plan tarihleri de eksene KATILIR. Aksi halde plandan sonraya çekilen
    // bir görevde gölge çubuk eksenin SOLUNA (negatif x) düşüp sticky ad kolonunun
    // altında kayboluyordu — üstelik tam da sapmanın en büyük olduğu durumda.
    for (const b of baseline ?? []) {
      if (b.baseStart) idx.push(localDayIndex(new Date(b.baseStart)));
      if (b.baseEnd) idx.push(localDayIndex(new Date(b.baseEnd)));
    }
    const today = localDayIndex(new Date());
    let from = idx.length === 0 ? today - 7 : Math.min(...idx) - 2;
    const to = idx.length === 0 ? today + 21 : Math.max(...idx) + 2;
    // HAFTA ölçeğinde başlangıç PAZARTESİYE çekilir. Aksi halde başlık hücreleri
    // yalnız pazartesilerde üretildiği için baştaki günlerin üstü BOŞ kalıyor ve
    // sonraki tüm etiketler kayıyordu.
    if (scale === "week") {
      const dow = (dayFromIndex(from).getDay() + 6) % 7; // Pzt=0
      from -= dow;
    }
    return { from, to };
  }, [tasks, scale, baseline]);

  const dayCount = Math.max(1, range.to - range.from + 1);
  const timelineW = dayCount * dayW;
  const todayX = (localDayIndex(new Date()) - range.from) * dayW;
  // "tarih yok" etiketinin güvenli x'i — zaman ekseni dışına taşmasın.
  const nodateX = Math.min(Math.max(0, todayX), Math.max(0, timelineW - 70));

  /** Görevin çubuğu: [sol px, genişlik px] — tarihi eksikse null. */
  function barOf(t: GanttTask): { x: number; w: number } | null {
    if (!t.plannedStart && !t.plannedEnd) return null;
    const s = t.plannedStart ? localDayIndex(new Date(t.plannedStart)) : localDayIndex(new Date(t.plannedEnd!));
    // Bozuk aralık (bitiş < başlangıç) NEGATİF genişlik üretmemeli — sunucu artık
    // engelliyor ama eski kayıtlar için savunma; en az tek günlük çubuk çizilir.
    const e = t.plannedEnd ? Math.max(s, localDayIndex(new Date(t.plannedEnd))) : s;
    const x = (s - range.from) * dayW;
    // Bitiş günü DAHİL — 1 günlük görev de görünür genişlikte olmalı.
    const w = Math.max(dayW * 0.6, (e - s + 1) * dayW);
    return { x, w };
  }

  const rowIndex = useMemo(() => new Map(tasks.map((t, i) => [t.id, i])), [tasks]);
  const cpm = useMemo(() => new Map((schedule ?? []).map((r) => [r.taskId, r])), [schedule]);
  const base = useMemo(() => new Map((baseline ?? []).map((r) => [r.taskId, r])), [baseline]);
  const criticalCount = (schedule ?? []).filter((r) => r.isCritical).length;

  /** Temel plandaki (dondurulmuş) çubuk — bugünkü planın ALTINA gölge olarak çizilir. */
  function baseBarOf(id: string): { x: number; w: number } | null {
    const b = base.get(id);
    if (!b || (!b.baseStart && !b.baseEnd)) return null;
    const s0 = localDayIndex(new Date(b.baseStart ?? b.baseEnd!));
    const e0 = Math.max(s0, localDayIndex(new Date(b.baseEnd ?? b.baseStart!)));
    return { x: (s0 - range.from) * dayW, w: Math.max(dayW * 0.6, (e0 - s0 + 1) * dayW) };
  }

  // Başlık hücreleri: gün ölçeğinde her gün, hafta ölçeğinde yalnız pazartesiler.
  const headCells = useMemo(() => {
    const cells: Array<{ i: number; label: string; month: string | null; weekend: boolean; first: boolean }> = [];
    for (let i = 0; i < dayCount; i++) {
      const d = dayFromIndex(range.from + i);
      const dow = d.getDay();
      const isMon = dow === 1;
      if (scale === "week" && !isMon) continue;
      cells.push({
        i,
        label: scale === "day" ? String(d.getDate()) : `${d.getDate()}`,
        month: d.getDate() <= (scale === "day" ? 1 : 7) || i === 0 ? `${MONTHS[d.getMonth()]} ${d.getFullYear()}` : null,
        weekend: dow === 0 || dow === 6,
        first: i === 0,
      });
    }
    return cells;
  }, [dayCount, range.from, scale]);

  /**
   * Bağımlılık oku: öncülün SAĞ kenarından ardılın SOL kenarına dik açılı yol.
   * İki ucun da çubuğu yoksa çizilmez (tarihi olmayan görev için ok anlamsız).
   */
  const arrows = useMemo(() => {
    const out: Array<{ id: string; d: string; violated: boolean; critical: boolean; title: string }> = [];
    const barCache = new Map<string, { x: number; w: number } | null>();
    const bar = (id: string) => {
      if (!barCache.has(id)) {
        const t = tasks.find((x) => x.id === id);
        barCache.set(id, t ? barOf(t) : null);
      }
      return barCache.get(id) ?? null;
    };
    for (const dep of deps) {
      const pi = rowIndex.get(dep.predecessorId);
      const si = rowIndex.get(dep.successorId);
      if (pi === undefined || si === undefined) continue;
      const pb = bar(dep.predecessorId);
      const sb = bar(dep.successorId);
      if (!pb || !sb) continue;
      const py = pi * ROW_H + ROW_H / 2;
      const sy = si * ROW_H + ROW_H / 2;
      const px = pb.x + pb.w;
      const sx = sb.x;
      const gap = 10;
      // Ardıl öncülün solunda kalıyorsa (ihlal) ok geri döner — bunu görünür kılmak
      // için çubukların ALTINDAN dolaşan bir yol çizilir.
      const midX = sx > px + gap ? sx - gap : px + gap;
      const dip = sx > px + gap ? sy : Math.max(py, sy) + ROW_H / 2 - 4;
      const d = sx > px + gap
        ? `M ${px} ${py} H ${midX} V ${sy} H ${sx}`
        : `M ${px} ${py} H ${px + gap} V ${dip} H ${sx - gap} V ${sy} H ${sx}`;
      out.push({
        id: dep.id, d, violated: dep.violated,
        // İki ucu da kritikse bağ kritik yolun parçasıdır — kalın çizilir.
        critical: !!(cpm.get(dep.predecessorId)?.isCritical && cpm.get(dep.successorId)?.isCritical),
        title: dep.violated ? `${dep.type} — tarihlerle ÇELİŞİYOR` : dep.type,
      });
    }
    return out;
  }, [deps, tasks, rowIndex, dayW, range.from, cpm]);

  const violatedCount = deps.filter((d) => d.violated).length;

  return (
    <div className="gnt">
      <div className="gnt-bar">
        <div className="gnt-scale" role="group" aria-label="Zaman ölçeği">
          {(["day", "week"] as Scale[]).map((s) => (
            <button key={s} type="button" className={`gnt-scale-btn ${scale === s ? "active" : ""}`}
              onClick={() => setScale(s)}>{s === "day" ? "Gün" : "Hafta"}</button>
          ))}
        </div>
        <button type="button" className="btn-md ghost" onClick={() => {
          // Bugünü görünür alanın soluna yakın getir.
          const el = scrollRef.current;
          if (el) el.scrollTo({ left: Math.max(0, todayX - 120), behavior: "smooth" });
        }}>
          <Icon name="calendar" size={14} />Bugüne git
        </button>
        <div className="grow" />
        {schedule && schedule.length > 0 ? (
          <label className="gnt-crit-toggle">
            <input type="checkbox" checked={showCritical} onChange={(e) => onToggleCritical(e.target.checked)} />
            <span>Kritik yolu vurgula</span>
            {criticalCount > 0 ? <b className="mono">{criticalCount}</b> : null}
          </label>
        ) : null}
        {baselineName ? (
          <span className="gnt-base-tag" title="Temel plan gölgesi gösteriliyor">
            <Icon name="archive" size={11} />{baselineName}
          </span>
        ) : null}
        {violatedCount > 0 ? (
          <span className="badge-pill b-danger" title="Bu bağlar mevcut tarihlerle tutmuyor">
            <Icon name="alertTriangle" size={11} />{violatedCount} çelişen bağ
          </span>
        ) : null}
        <span className="gnt-legend">
          <span className="gnt-legend-item"><i className="sw bar" />Planlanan</span>
          <span className="gnt-legend-item"><i className="sw prog" />İlerleme</span>
          <span className="gnt-legend-item"><i className="sw ms" />Kilometre taşı</span>
        </span>
      </div>

      <div className="gnt-scroll" ref={scrollRef}>
        <div className="gnt-inner" style={{ width: NAME_W + timelineW }}>
          {/* Başlık — sticky top; sol köşe hem top hem left sticky */}
          <div className="gnt-head" style={{ height: 38 }}>
            <div className="gnt-head-name" style={{ width: NAME_W }}>Görev</div>
            <div className="gnt-head-days" style={{ width: timelineW }}>
              {headCells.map((c) => (
                <div key={c.i} className={`gnt-head-cell ${c.weekend ? "we" : ""}`}
                  style={{ left: c.i * dayW, width: scale === "day" ? dayW : dayW * 7 }}>
                  {c.month ? <b className="gnt-head-month">{c.month}</b> : null}
                  <span className="mono">{c.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="gnt-rows" style={{ height: tasks.length * ROW_H }}>
            {/* Hafta sonu şeritleri + bugün çizgisi — çubukların ALTINDA */}
            <div className="gnt-bg" style={{ left: NAME_W, width: timelineW }}>
              {Array.from({ length: dayCount }, (_, i) => {
                const dow = dayFromIndex(range.from + i).getDay();
                return dow === 0 || dow === 6
                  ? <span key={i} className="gnt-weekend" style={{ left: i * dayW, width: dayW }} />
                  : null;
              })}
              {todayX >= 0 && todayX <= timelineW ? (
                <span className="gnt-today" style={{ left: todayX }} title="Bugün" />
              ) : null}
            </div>

            {tasks.map((t, i) => {
              const b = barOf(t);
              const m = STATUS_META.task[t.status as TaskStatus];
              return (
                <div key={t.id} className="gnt-row" style={{ top: i * ROW_H, height: ROW_H }}>
                  <div className="gnt-name" style={{ width: NAME_W }}>
                    <button type="button" className="gnt-name-btn" style={{ paddingLeft: 8 + t.depth * 14 }}
                      onClick={() => onOpenTask(t.id)} title={`${t.code} — ${t.title}`}>
                      <span className="gnt-wbs mono">{t.wbs}</span>
                      <span className="gnt-title">{t.title}</span>
                      {showCritical && cpm.get(t.id)?.isCritical ? (
                        <i className="gnt-crit-dot" title="Kritik yolda" aria-hidden="true" />
                      ) : null}
                    </button>
                  </div>
                  <div className="gnt-track" style={{ width: timelineW }}>
                    {/* Temel plan gölgesi HER DURUMDA çizilir: görevin bugünkü tarihi
                        silinmiş olsa ya da kilometre taşı olsa bile. Eskiden yalnız
                        normal çubuk dalındaydı; tam da sapmanın en büyük olduğu
                        (tarih silinmiş) durumda hiçbir şey göstermiyordu. */}
                    {(() => {
                      const bb = baseBarOf(t.id);
                      return bb ? <span className="gnt-basebar" style={{ left: bb.x, width: bb.w }} title="Temel plan" /> : null;
                    })()}
                    {b === null ? (
                      // Etiket zaman ekseni İÇİNDE kalmalı: `todayX` aralık dışındaysa
                      // (tüm görevler geçmişte, bugün çok ileride) binlerce piksel sağa
                      // düşüp yatay kaydırmayı şişiriyordu.
                      <span className="gnt-nodate" style={{ left: nodateX }}>tarih yok</span>
                    ) : t.isMilestone ? (
                      <span className={`gnt-ms ${m.cls}`} style={{ left: b.x + b.w / 2 - 7 }}
                        title={`${t.title} — kilometre taşı`} onClick={() => onOpenTask(t.id)} />
                    ) : (
                      <>
                        <div
                          className={`gnt-barx ${m.cls} ${showCritical && cpm.get(t.id)?.isCritical ? "is-crit" : ""}`}
                          style={{ left: b.x, width: b.w }}
                          title={(() => {
                            const c = cpm.get(t.id);
                            const bd = base.get(t.id)?.endDrift;
                            const parts = [`${t.code} · ${m.label} · %${t.progressPct}`];
                            if (c && !c.unscheduled) parts.push(c.isCritical ? "KRİTİK YOL" : `bolluk: ${c.slackDays} iş günü`);
                            if (c?.constraintIncomplete) parts.push("⚠ öncül kısıtı hesaba katılamadı");
                            if (bd != null && bd !== 0) parts.push(`temel plana göre ${bd > 0 ? "+" : ""}${bd} gün`);
                            return parts.join(" · ");
                          })()}
                          onClick={() => onOpenTask(t.id)}
                        >
                          <span className="gnt-fill" style={{ width: `${t.progressPct}%` }} />
                          {b.w > 46 ? <span className="gnt-barlbl mono">%{t.progressPct}</span> : null}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Ok katmanı — çubukların ÜSTÜNDE ama tıklamayı geçirir */}
            <svg className="gnt-arrows" style={{ left: NAME_W, width: timelineW, height: tasks.length * ROW_H }}
              aria-hidden="true">
              <defs>
                <marker id="gnt-ah" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
                  <path d="M0,0 L7,3.5 L0,7 z" fill="var(--neutral-500)" />
                </marker>
                <marker id="gnt-ah-bad" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
                  <path d="M0,0 L7,3.5 L0,7 z" fill="var(--danger)" />
                </marker>
              </defs>
              {arrows.map((a) => (
                <path key={a.id} d={a.d} className={`gnt-arrow ${a.violated ? "bad" : ""} ${showCritical && a.critical ? "crit" : ""}`}
                  markerEnd={`url(#${a.violated ? "gnt-ah-bad" : "gnt-ah"})`}>
                  <title>{a.title}</title>
                </path>
              ))}
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}
